import { useEffect, useEffectEvent, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  applyShareSnapshotDiff,
  diffShareSnapshots,
  fetchShareSnapshot,
  SHARE_RECONCILE_INTERVAL_MS,
  shareSnapshotKey,
  type ShareSnapshot,
} from './share-access-reconciliation';

/**
 * Re-reads the person's active shares every minute while the tab is visible,
 * on focus, and whenever the caller invalidates the snapshot (realtime
 * reconnect), and evicts what each ended or lowered share left cached. A failed
 * read changes nothing; the next one retries.
 */
export function useShareAccessReconciliation(params: {
  readonly organizationId: string | undefined;
  readonly userId: string | undefined;
  readonly onRevoked: (correspondenceId: string, organizationId: string) => void;
}): void {
  const { organizationId, userId, onRevoked } = params;
  const queryClient = useQueryClient();
  const enabled = !!organizationId && !!userId;

  const { data } = useQuery({
    queryKey: shareSnapshotKey(organizationId ?? '', userId ?? ''),
    queryFn: async () => await fetchShareSnapshot(organizationId ?? '', userId ?? ''),
    enabled,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: 'always',
    refetchInterval: SHARE_RECONCILE_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const baseline = useRef<{ readonly scope: string; readonly snapshot: ShareSnapshot } | null>(
    null,
  );

  const notifyRevoked = useEffectEvent((correspondenceId: string, orgId: string) => {
    onRevoked(correspondenceId, orgId);
  });

  useEffect(() => {
    if (!organizationId || !userId || !data) {
      return;
    }
    const scope = `${organizationId}:${userId}`;
    const previous = baseline.current;
    baseline.current = { scope, snapshot: data };
    if (previous?.scope !== scope) {
      return;
    }
    const diff = diffShareSnapshots(queryClient, organizationId, previous.snapshot, data);
    applyShareSnapshotDiff(queryClient, organizationId, diff, (correspondenceId) => {
      notifyRevoked(correspondenceId, organizationId);
    });
  }, [data, organizationId, userId, queryClient]);
}
